import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useLanguage } from '../context/LanguageContext';
import { menuCategories } from '../data/menuData';

const MenuSection = () => {
  const { language, t } = useLanguage();
  const [activeCategory, setActiveCategory] = useState('broodjes');

  const activeItems = menuCategories.find(cat => cat.id === activeCategory)?.items || [];

  return (
    <section id="menu" className="py-20 bg-[#1A1816]" data-testid="menu-section">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="text-center mb-12"
        >
          <h2 className="font-playfair text-3xl sm:text-4xl lg:text-5xl font-bold text-[#FDFBF7] mb-4">
            {t('menu.title')}
          </h2>
          <p className="font-manrope text-lg text-[#FDFBF7]/70 mb-6">
            {t('menu.subtitle')}
          </p>
          <div className="w-24 h-1 bg-[#9F3E1F] mx-auto rounded-full" />
        </motion.div>

        {/* Category Tabs */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="flex flex-wrap justify-center gap-2 mb-12"
        >
          {menuCategories.map((category) => (
            <motion.button
              key={category.id}
              onClick={() => setActiveCategory(category.id)}
              className={`px-4 py-2 rounded-full font-manrope font-medium text-sm transition-all ${
                activeCategory === category.id
                  ? 'bg-[#9F3E1F] text-white shadow-lg shadow-[#9F3E1F]/20'
                  : 'bg-[#2D2A26] text-[#FDFBF7] hover:bg-[#3D3A36]'
              }`}
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              data-testid={`menu-tab-${category.id}`}
            >
              <span className="mr-1">{category.icon}</span>
              {category.name[language]}
            </motion.button>
          ))}
        </motion.div>

        {/* Menu Items Grid */}
        <AnimatePresence mode="wait">
          <motion.div
            key={activeCategory}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -20 }}
            transition={{ duration: 0.3 }}
            className="grid md:grid-cols-2 lg:grid-cols-3 gap-4"
            data-testid="menu-items-grid"
          >
            {activeItems.map((item, index) => (
              <motion.div
                key={item.id}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.05 }}
                className={`rounded-xl p-5 flex justify-between items-center transition-colors group border border-[#FDFBF7]/5 ${
                  item.price === null 
                    ? 'bg-[#9F3E1F]/20 justify-center' 
                    : 'bg-[#2D2A26] hover:bg-[#3D3A36]'
                }`}
                whileHover={item.price !== null ? { scale: 1.02 } : {}}
                data-testid={`menu-item-${item.id}`}
              >
                <div className={item.price === null ? 'text-center w-full' : 'flex-1'}>
                  <h4 className={`font-manrope font-semibold transition-colors ${
                    item.price === null 
                      ? 'text-[#D4AF37] text-lg' 
                      : 'text-[#FDFBF7] group-hover:text-[#9F3E1F]'
                  }`}>
                    {item.name}
                  </h4>
                </div>
                {item.price !== null && (
                  <div className="flex items-center gap-3">
                    <span className="font-playfair text-xl font-bold text-[#D4AF37]">
                      €{item.price.toFixed(2)}
                    </span>
                  </div>
                )}
              </motion.div>
            ))}
          </motion.div>
        </AnimatePresence>

        {/* Halal Notice */}
        <motion.div
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          viewport={{ once: true }}
          className="mt-12 text-center"
        >
          <div className="inline-flex items-center gap-2 bg-[#D4AF37]/10 border border-[#D4AF37]/30 px-6 py-3 rounded-full">
            <span className="text-[#D4AF37] text-xl">☪</span>
            <span className="font-manrope font-medium text-[#D4AF37]">
              100% Halal Gecertificeerd
            </span>
          </div>
        </motion.div>
      </div>
    </section>
  );
};

export default MenuSection;
